import { FC, useEffect, useMemo } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Async
import { getInvoicesSummaryBreakdown } from "app/store/BillingInvoices/BillingInvoices.async";
// Actions
import { BillingInvoicesActions } from "app/store/BillingInvoices/BillingInvoices.slice";
// Selectors
import { selectInvoicesSummaryBreakdown } from "app/store/BillingInvoices/BillingInvoices.selectors";
// MUI
import { Box, Toolbar, Typography } from "@mui/material";
// Utilities
import { toNumberWithCommas } from "app/utilities/Utilities";

const InvoicesSummary: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const invoicesSummaryBreakdown = useAppSelector(selectInvoicesSummaryBreakdown);

  useEffect(() => {
    dispatch(getInvoicesSummaryBreakdown({}));

    return () => {
      dispatch(BillingInvoicesActions.setInitialField('invoicesSummaryBreakdown'));
    };
    // eslint-disable-next-line
  }, []);

  const items = useMemo(() => {
    if (!invoicesSummaryBreakdown) return [];
    return [
      { key: 'outstanding', label: t('labels.outstanding'), color: 'primary.main', ...invoicesSummaryBreakdown.outstanding },
      { key: 'overdue', label: t('labels.overdue'), color: 'orange', ...invoicesSummaryBreakdown.overdue },
      { key: 'upcoming', label: t('labels.upcoming'), color: 'gray', ...invoicesSummaryBreakdown.upcoming },
    ]
  }, [t, invoicesSummaryBreakdown]);

  if (!invoicesSummaryBreakdown) return null;
  return (
    <Toolbar sx={{ width: '100%', justifyContent: 'center', gap: 1 }}>
      {items.map((item) => (
        <Box key={item.key} sx={{ width: '280px' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {`$${toNumberWithCommas(item.amount)}`}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
            {`${toNumberWithCommas(item.count, false)} ${item.label} ${t('labels.invoices')}`}
          </Typography>
          <Box sx={{
            height: 12,
            bgcolor: item.color,
            borderRadius: 1,
          }}/>
        </Box>
      ))}
    </Toolbar>
  );
};

export default InvoicesSummary;
